import { FC } from "react";
import {
    BaseEdge, EdgeLabelRenderer, EdgeProps, Position,
    getBezierPath, getSimpleBezierPath, getSmoothStepPath, getStraightPath,
} from "@xyflow/react";
import { EdgeContent } from "../nodeTypes/common";
import { defaultEdgeOptions } from "../layout";
import { buildPath, casingSegments, CASING_WIDTH_MARGIN, CORNER_RADIUS, labelAnchor, type Corners } from "./path";
import type { Hop } from "../crossings";
import type { Point } from "../layout";
import React from "react";
import clsx from "clsx";

type PathParams = {
    sourceX: number; sourceY: number; sourcePosition: Position;
    targetX: number; targetY: number; targetPosition: Position;
};

/** react-flow's path helpers all return `[path, labelX, labelY, offsetX, offsetY]`. */
type PathResult = [path: string, labelX: number, labelY: number, offsetX: number, offsetY: number];

/**
 * How an edge is drawn.
 *
 * `type` no longer selects a path ALGORITHM -- the layout engine routes every edge, so the type
 * selects how those routes are cornered.  `getPath` is only the fallback, for the frames before a
 * layout has resolved and for a manual layout, which the engine cannot route.
 */
export type EdgeTypeConfig = {
    /** Name shown in React DevTools and in component-stack warnings. */
    displayName: string;
    /** Corner treatment applied at the router's bend points. */
    corners: Corners;
    /** Corner radius in px. Clamped per corner to half the shorter adjoining segment. */
    borderRadius?: number;
    getPath: (params: PathParams) => PathResult;
};

/**
 * The label's box, shown only when something renders in it.  A consumer's `getEdgeContent` may
 * render an empty body, which with a background and padding would sit on the line as a blank pill,
 * so the rendered content is checked after each commit.
 */
const EdgeLabel: FC<{ style: React.CSSProperties; className: string; data: unknown; children: React.ReactNode }> =
    ({ style, className, data, children }) => {
        const box = React.useRef<HTMLDivElement>(null);
        const [empty, setEmpty] = React.useState(false);

        React.useLayoutEffect(() => {
            const element = box.current;
            setEmpty(!element || (!(element.textContent ?? "").trim() && !element.querySelector("svg, img, canvas, input, button")));
        }, [data]);

        return (
            <div ref={box} style={style} className={clsx(className, empty && "FlowChart-edge-label-content--empty")}>
                {children}
            </div>
        );
    };

export function createEdgeType(config: EdgeTypeConfig) {
    const { displayName, getPath } = config;
    const Edge: FC<EdgeProps> = (props: EdgeProps) => {
        const {
            id,
            sourceX,
            sourceY,
            targetX,
            targetY,
            sourcePosition,
            targetPosition,
            data,
            style,
            label,
            labelStyle,
            labelShowBg,
            labelBgStyle,
            labelBgPadding,
            labelBgBorderRadius,
            markerStart,
            markerEnd,
            interactionWidth,
        } = props;

        // `routing: "direct"` opts an individual link out of the computed route and back onto a
        // plain point-to-point path, whatever its type.
        const route = data?.routing === "direct" ? undefined : (data?.route as Point[] | undefined);

        // The chart decides the corner treatment (`layoutOptions.corners`, via `data`); only
        // `mixed` leaves it to the link's type.  A chord has no corners either way.
        const corners: Corners = config.corners === "none"
            ? "none"
            : ((data?.corners as Corners | undefined) ?? config.corners);
        const borderRadius = corners === "rounded"
            ? ((data?.cornerRadius as number | undefined) ?? config.borderRadius ?? CORNER_RADIUS)
            : 0;

        // Crossings this link is responsible for indicating, assigned centrally: a crossing is a
        // property of a PAIR of links, so it cannot be worked out from inside one of them.
        const hops = (data?.hops as Hop[] | undefined) ?? [];
        const crossing = data?.crossing as string | undefined;
        const bridges = crossing === "hop" ? hops : [];
        const casings = crossing === "casing" ? hops : [];

        let edgePath: string;
        let labelX: number;
        let labelY: number;

        if (route && route.length >= 2) {
            edgePath = buildPath(route, corners, borderRadius, bridges);
            const anchor = labelAnchor(route);
            labelX = anchor.x;
            labelY = anchor.y;
        } else {
            // No route yet (or a manual layout): fall back to react-flow's own path, which is
            // exactly how this rendered before the layout engine produced routes.
            [edgePath, labelX, labelY] = getPath({
                sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition,
            });
        }

        const edgeWidth = Math.abs(targetX - sourceX);
        const maxContentWidth = edgeWidth >= 64 ? edgeWidth - 32 : 120; // Horizontal connection: width - 32px padding, Vertical connection: 120px (average node width)

        return (
            <>
                {/*
                  * Props are listed explicitly rather than spread.  In v11 `BaseEdge` destructures
                  * a fixed list and silently ignores anything else, so a spread was inert; in v12
                  * it forwards the remainder onto the `<path>`, where `source`, `target`,
                  * `selected`, `animated` and friends would become invalid DOM attributes.  This
                  * list is exactly what `BaseEdge` reads, so it behaves identically under both.
                  */}
                {/*
                  * Casings first, so they cover the strokes of every edge painted before this one
                  * and are themselves covered by this edge's own stroke. That ordering is the
                  * entire mechanism, and it rests on every edge painting at the same depth: the
                  * chart renders react-flow in `zIndexMode="manual"` so that array order decides,
                  * so never set `zIndex` on an edge, which would hoist it out of that order.
                  *
                  * `style` is deliberately NOT spread here: it carries the caller's dash pattern,
                  * and markers must not be repeated onto a background-coloured stroke.
                  */}
                {casings.length > 0 && route && casingSegments(route, casings, corners, borderRadius).map((segment, i) => (
                    <path
                        key={i}
                        className="FlowChart-edge-casing"
                        d={segment}
                        strokeWidth={(Number((style as any)?.strokeWidth) || defaultEdgeOptions.width) + CASING_WIDTH_MARGIN}
                    />
                ))}
                <BaseEdge
                    id={id}
                    path={edgePath}
                    style={style}
                    label={label}
                    labelStyle={labelStyle}
                    labelShowBg={labelShowBg}
                    labelBgStyle={labelBgStyle}
                    labelBgPadding={labelBgPadding}
                    labelBgBorderRadius={labelBgBorderRadius}
                    markerStart={markerStart}
                    markerEnd={markerEnd}
                    interactionWidth={interactionWidth}
                />
                <EdgeLabelRenderer>
                    <EdgeLabel
                        style={{
                            position: "absolute",
                            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                            maxWidth: maxContentWidth,
                            ...style,
                        }}
                        className={clsx("FlowChart-edge-label-content", "nodrag", "nopan", data?.className)}
                        data={data}
                    >
                        <EdgeContent {...props} />
                    </EdgeLabel>
                </EdgeLabelRenderer>
            </>
        );
    };

    Edge.displayName = displayName;
    return Edge;
}

/**
 * Both rounded treatments draw at the one chart radius, clamped per route.  Charts use both names.
 */
const ROUNDED_RADIUS = CORNER_RADIUS;
const CURVED_RADIUS = CORNER_RADIUS;

/*
 * The five previously documented type names all survive, and `orthogonal` joins them.  They
 * select a corner treatment over the router's route rather than a path algorithm, which keeps the
 * visual language of each one intact -- most importantly `smoothstep`, which every existing chart
 * uses and which was already an orthogonal step path with 5px rounded corners.
 *
 * The configs are exported (internally, see the foot of the file) so the tests can assert on the
 * real thing rather than restate these numbers -- in particular that `orthogonal` and
 * `smoothstep` stay identical.
 */
const SMOOTHSTEP: EdgeTypeConfig = {
    displayName: "SmoothStep",
    corners: "rounded",
    borderRadius: ROUNDED_RADIUS,
    getPath: getSmoothStepPath,
};
/** Says what it means; the same thing `smoothstep` has always done. */
const ORTHOGONAL: EdgeTypeConfig = { ...SMOOTHSTEP, displayName: "Orthogonal" };

const STEP: EdgeTypeConfig = {
    displayName: "Step",
    corners: "sharp",
    getPath: params => getSmoothStepPath({ ...params, borderRadius: 0 }),
};
const BEZIER: EdgeTypeConfig = {
    displayName: "Bezier",
    // Smoothing the route rather than ignoring it: a true curve between the endpoints would
    // reintroduce the node overlaps the router exists to avoid, and would leave two different
    // rendering regimes in one diagram.  So this is the rounded treatment at a wider radius --
    // which is all `"curved"` ever meant, and why it is no longer a corner mode of its own.
    corners: "rounded",
    borderRadius: CURVED_RADIUS,
    getPath: getBezierPath,
};
const SIMPLE_BEZIER: EdgeTypeConfig = { ...BEZIER, displayName: "SimpleBezier", getPath: getSimpleBezierPath };

const STRAIGHT: EdgeTypeConfig = {
    displayName: "Straight",
    // Deliberately discards the route: this is the documented escape hatch for a direct chord.
    corners: "none",
    // `getStraightPath` takes only the four coordinates — the two Position values are dropped.
    getPath: ({ sourceX, sourceY, targetX, targetY }) => getStraightPath({ sourceX, sourceY, targetX, targetY }),
};

export const SmoothStep = createEdgeType(SMOOTHSTEP);
export const Orthogonal = createEdgeType(ORTHOGONAL);
export const Step = createEdgeType(STEP);
export const Bezier = createEdgeType(BEZIER);
export const SimpleBezier = createEdgeType(SIMPLE_BEZIER);
export const Straight = createEdgeType(STRAIGHT);

/**
 * Exported for `path.test.ts` only.  Not published: `library_build` sets `stripInternal`, so this
 * statement is dropped from the emitted `.d.ts` -- see the note at the foot of `layout.ts`.
 *
 * @internal
 */
export { SMOOTHSTEP, ORTHOGONAL, STEP, BEZIER, SIMPLE_BEZIER, STRAIGHT };
